import { useMemo, useState } from "react";
import { isSelectAll } from "Components/SbJdCreationDrawerV2/helpers";

// Org placement cascade (Business Unit → Function → Sub-function) over
// orgMetaData.organization_structure — the same tree SbJdCreationDrawerV2/
// useJdForm cascades over, but single-select and form-free. Business Unit
// is aggregated across every legal entity (no Legal Entity field in this
// form), so its options come straight from flattening entities' business
// units rather than from childrenFor keyed on a chosen entity. The field
// config below is the single source of truth for everything downstream of
// that: rendering order, the disabled-until-parent-picked rule, which
// descendants get cleared when a parent changes, and (via jobField) how
// each selection filters a published job.

export const ORG_PLACEMENT_FIELDS = [
  { name: "businessUnit", label: "Business Unit", parent: null, jobField: "business_unit" },
  { name: "functionDepartment", label: "Function", parent: "businessUnit", jobField: "function" },
  { name: "subFunction", label: "Sub-function", parent: "functionDepartment", jobField: "sub_function" },
];

const FIELD_NAMES = ORG_PLACEMENT_FIELDS.map((field) => field.name);

const descendantsOf = (name) =>
  ORG_PLACEMENT_FIELDS.filter((field) => field.parent === name).flatMap((child) => [
    child.name,
    ...descendantsOf(child.name),
  ]);

// name → every field downstream of it in the tree, precomputed once.
const DESCENDANTS = Object.fromEntries(FIELD_NAMES.map((name) => [name, descendantsOf(name)]));

const toOptions = (items, key) =>
  [...new Set((items ?? []).map((item) => item?.[key]).filter(Boolean))].map((value) => ({
    value,
    label: value,
  }));

// The org tree carries a sentinel sibling at every level (e.g. "ALL - All")
// whose own children array contains only itself (see isSelectAll). Picking a
// real parent just returns that parent's children as-is; picking the
// sentinel instead aggregates children across every REAL sibling, so e.g.
// choosing Function = "ALL - All" still offers every real sub-function
// across the business unit rather than collapsing to the sentinel alone. If
// there are no real siblings to aggregate (the sentinel is the only entry),
// fall back to its own children so the level isn't left with zero options —
// same fallback expandSentinels uses.
//
// The non-sentinel branch flatMaps every matching node rather than taking
// the first — Business Unit is now aggregated across all legal entities
// (see buildBusinessUnits below), so two entities can each contribute a
// node with the same business_unit name, and both must keep their own
// functions rather than one shadowing the other.
const childrenFor = (nodes, parentKey, parentValue, childrenKey) => {
  if (!Array.isArray(nodes)) return [];
  if (isSelectAll(parentValue)) {
    const realSiblings = nodes.filter((node) => !isSelectAll(node?.[parentKey]));
    if (realSiblings.length) return realSiblings.flatMap((node) => node?.[childrenKey] ?? []);
    return nodes.find((node) => isSelectAll(node?.[parentKey]))?.[childrenKey] ?? [];
  }
  return nodes.filter((node) => node?.[parentKey] === parentValue).flatMap((node) => node?.[childrenKey] ?? []);
};

// Business Unit has no Legal Entity field to key off of in this form, so its
// options are every entity's business_units flattened together (duplicate
// names collapse via toOptions' Set, same as any other level).
const buildBusinessUnits = (entities) =>
  entities.flatMap((entity) => (Array.isArray(entity?.business_units) ? entity.business_units : []));

// jd_metadata_json stores multi-select function/sub_function comma-joined
// (useJdForm's joinMulti), and option names can themselves contain commas —
// so membership is checked against ", "-delimited boundaries rather than by
// splitting (same ambiguity trade-off as useJdForm's resolveMulti).
const includesJoinedName = (joined, name) => {
  const normalized = String(joined || "")
    .split(",")
    .map((part) => part.trim())
    .join(", ");
  return `, ${normalized}, `.includes(`, ${name}, `);
};

// True when a /published-jobs row is consistent with every placement field
// selected so far — unselected fields (and fields with no jobField) don't
// filter, so the Target Role list narrows progressively as the user picks.
// A job that doesn't carry a given jobField at all (not every job posting is
// necessarily tagged down to sub-function) can't be judged against it either
// way — treated as a non-match it would silently collapse the Target Role
// list to empty for every job once the cascade is fully picked, so it's
// treated as "can't disqualify" and left in instead. The backend sends ""
// rather than null for a genuinely missing field, so an empty string gets
// the same treatment — otherwise every job missing that field would be
// silently excluded the moment a user picks any real value at that level.
export const placementMatchesJob = (placement, job) =>
  ORG_PLACEMENT_FIELDS.every(({ name, jobField }) => {
    if (!jobField || placement[name] == null || isSelectAll(placement[name])) return true;
    const jobValue = job?.[jobField];
    return jobValue == null || jobValue === "" || includesJoinedName(jobValue, placement[name]);
  });

const useOrgPlacement = (orgMetaData, initialValues) => {
  const [placement, setPlacement] = useState(() =>
    Object.fromEntries(FIELD_NAMES.map((name) => [name, initialValues?.[name] ?? null]))
  );

  const options = useMemo(() => {
    const entities = Array.isArray(orgMetaData?.organization_structure)
      ? orgMetaData.organization_structure
      : [];
    const businessUnits = buildBusinessUnits(entities);
    const functions = childrenFor(businessUnits, "business_unit", placement.businessUnit, "functions");
    const subFunctions = childrenFor(functions, "function", placement.functionDepartment, "sub_functions");
    return {
      businessUnit: toOptions(businessUnits, "business_unit"),
      functionDepartment: toOptions(functions, "function"),
      subFunction: toOptions(subFunctions, "sub_function"),
    };
  }, [orgMetaData, placement]);

  // Selecting (or clearing) a field always invalidates everything downstream.
  const setField = (name, value) =>
    setPlacement((prev) => ({
      ...prev,
      [name]: value ?? null,
      ...Object.fromEntries(DESCENDANTS[name].map((child) => [child, null])),
    }));

  return { placement, options, setField };
};

export default useOrgPlacement;
